import { 
  Zap, Sparkles, BookOpen, Edit2, Map, Headphones, 
  Wand2, BarChart3, BookMarked, Target, Users, Pause, Play, RefreshCw,
  ChevronLeft, ChevronRight, RotateCcw, RotateCw, Shield, FileText, Eye, Image,
  MessageCircle, Moon, Sun, Keyboard, SkipBack, SkipForward
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel 
} from "../ui/dropdown-menu";

/**
 * WritingToolbar — 2-row mobile-friendly action bar with full labels
 */
export const WritingToolbar = ({
  onQuickFix, onSmartRefine, onStoryDoctor, onStoryBible,
  onChapterEditor, hasChapters, onStoryNavigator, onStoryArchitect,
  onAudioPlay, onAudioPause, isAudioPlaying, isAudioLoading,
  canResumeAudio, onResumeAudio,
  currentAudioChapter, totalChapters, onPrevChapter, onNextChapter,
  onRewindParagraph, onForwardParagraph,
  onWritingTools, onAnalysisDashboard, onStoryDirection, onAnalyze, analyzing,
  onAIDetection, onSynopsisLadder, onCanonLock, onCharacterPortraits, onContactSupport,
  onFocusMode, focusModeActive, onFocusModeKindle, kindleModeActive,
  onFinalPolish,
  disabled
}) => {
  const isKindle = focusModeActive && kindleModeActive;
  const hasChaps = totalChapters > 0;

  return (
    <div 
      className={`sticky top-0 z-40 backdrop-blur-md shadow-lg transition-colors duration-300 ${
        isKindle 
          ? 'bg-[#F4ECD8]/98 border-b border-amber-200' 
          : 'bg-[#080E1A]/98 border-b border-cyan-500/15'
      }`}
      data-testid="writing-toolbar"
    >
      {/* ══ ROW 1: Chapter Skip + Improve + Listen ══ */}
      <div className="px-3 sm:px-4 pt-2 pb-1">
        <div className="flex items-center gap-2">

          {/* ── Chapter Skip Navigation ── */}
          <div className={`flex items-center shrink-0 rounded-lg border ${isKindle ? 'border-amber-300 bg-amber-100/50' : 'border-slate-700/60 bg-slate-800/40'}`} data-testid="chapter-nav-group">
            <button
              onClick={onPrevChapter}
              disabled={disabled || isAudioLoading || !hasChaps || (currentAudioChapter ?? 1) <= 1}
              className={`h-10 px-2 rounded-l-lg transition-all disabled:opacity-30 ${isKindle ? 'text-amber-700 hover:bg-amber-200 active:bg-amber-300' : 'text-slate-400 hover:text-white hover:bg-slate-700 active:bg-slate-600'}`}
              title="Previous Chapter"
              data-testid="toolbar-prev-chapter-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className={`flex flex-col items-center px-2 min-w-[52px] border-x ${isKindle ? 'border-amber-300/50' : 'border-slate-700/40'}`}>
              <span className={`text-[9px] font-bold uppercase tracking-wider ${isKindle ? 'text-amber-600' : 'text-slate-500'}`}>Skip</span>
              <span className={`text-xs font-semibold ${isKindle ? 'text-amber-800' : 'text-cyan-400'}`}>
                {hasChaps ? `${currentAudioChapter ?? 1}/${totalChapters}` : '—'}
              </span>
            </div>
            <button
              onClick={onNextChapter}
              disabled={disabled || isAudioLoading || !hasChaps || (currentAudioChapter ?? 1) >= (totalChapters ?? 1)}
              className={`h-10 px-2 rounded-r-lg transition-all disabled:opacity-30 ${isKindle ? 'text-amber-700 hover:bg-amber-200 active:bg-amber-300' : 'text-slate-400 hover:text-white hover:bg-slate-700 active:bg-slate-600'}`}
              title="Next Chapter"
              data-testid="toolbar-next-chapter-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* ── IMPROVE dropdown ── */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={disabled}
                className={`h-10 px-3 flex items-center gap-1.5 rounded-lg font-semibold text-xs transition-all active:scale-95 border ${
                  isKindle
                    ? 'bg-violet-100 border-violet-300 text-violet-700 hover:bg-violet-200'
                    : 'bg-violet-900/50 border-violet-500/50 text-violet-200 hover:bg-violet-900/70 hover:border-violet-400/70'
                }`}
                data-testid="toolbar-improve-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span>Improve</span>
                <ChevronLeft className="w-3 h-3 rotate-[-90deg] opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 bg-[#0D1525] border border-violet-500/25 shadow-2xl">
              <DropdownMenuLabel className="text-violet-400 text-[10px] uppercase tracking-widest pb-1">Fix & Polish</DropdownMenuLabel>
              <DropdownMenuItem onClick={onStoryDoctor} className="cursor-pointer py-2.5 text-slate-200 hover:bg-violet-900/30 focus:bg-violet-900/30 gap-2" data-testid="toolbar-issue-scanner-item">
                <div className="w-7 h-7 rounded bg-rose-900/40 flex items-center justify-center shrink-0"><Sparkles className="w-3.5 h-3.5 text-rose-400" /></div>
                <div><p className="text-sm font-medium">Issue Scanner</p><p className="text-[10px] text-slate-500">Grammar, repetition, structure</p></div>
                <Badge className="ml-auto text-[9px] bg-slate-700/50 text-slate-400 border-slate-600/50">LOCAL</Badge>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onQuickFix} className="cursor-pointer py-2.5 text-slate-200 hover:bg-violet-900/30 focus:bg-violet-900/30 gap-2" data-testid="toolbar-quick-fix-item">
                <div className="w-7 h-7 rounded bg-violet-900/40 flex items-center justify-center shrink-0"><Zap className="w-3.5 h-3.5 text-violet-400" /></div>
                <div><p className="text-sm font-medium">Quick Fix (Basic)</p><p className="text-[10px] text-slate-500">Fast local text fixes, no AI</p></div>
                <Badge className="ml-auto text-[9px] bg-slate-700/50 text-slate-400 border-slate-600/50">LOCAL</Badge>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-violet-500/10" />
              <DropdownMenuLabel className="text-cyan-400 text-[10px] uppercase tracking-widest pb-1 pt-2">AI Rewrite</DropdownMenuLabel>
              <DropdownMenuItem onClick={onSmartRefine} disabled={!hasChapters} className="cursor-pointer py-2.5 text-slate-200 hover:bg-cyan-900/20 focus:bg-cyan-900/20 gap-2" data-testid="toolbar-smart-refine-item">
                <div className="w-7 h-7 rounded bg-gradient-to-br from-cyan-900/60 to-violet-900/60 flex items-center justify-center shrink-0"><Zap className="w-3.5 h-3.5 text-cyan-400" /></div>
                <div><p className="text-sm font-medium">Intelligent Rewrite (Nova)</p><p className="text-[10px] text-slate-500">Advanced AI editing & rewriting</p></div>
                <Badge className="ml-auto text-[9px] bg-cyan-900/40 text-cyan-400 border-cyan-500/30">AI</Badge>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onChapterEditor} disabled={!hasChapters} className="cursor-pointer py-2.5 text-slate-200 hover:bg-cyan-900/20 focus:bg-cyan-900/20 gap-2" data-testid="toolbar-chapter-editor-item">
                <div className="w-7 h-7 rounded bg-cyan-900/40 flex items-center justify-center shrink-0"><Edit2 className="w-3.5 h-3.5 text-cyan-400" /></div>
                <div><p className="text-sm font-medium">Edit Chapter</p><p className="text-[10px] text-slate-500">Open chapter line editor</p></div>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-violet-500/10" />
              <DropdownMenuItem onClick={onFinalPolish} className="cursor-pointer py-2.5 text-slate-200 hover:bg-violet-900/30 focus:bg-violet-900/30 gap-2">
                <div className="w-7 h-7 rounded bg-violet-900/40 flex items-center justify-center shrink-0"><Sparkles className="w-3.5 h-3.5 text-violet-400" /></div>
                <div><p className="text-sm font-medium">Final Polish (Pro)</p><p className="text-[10px] text-slate-500">Deep AI-powered refinement</p></div>
                <Badge className="ml-auto text-[9px] bg-amber-900/40 text-amber-400 border-amber-500/30">PRO</Badge>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* ── LISTEN / Audio ── */}
          <div className="flex items-center shrink-0 ml-auto">
            <button
              onClick={isAudioPlaying ? onAudioPause : (canResumeAudio ? onResumeAudio : onAudioPlay)}
              disabled={disabled || isAudioLoading}
              className={`h-10 px-3 flex items-center gap-1.5 rounded-lg font-semibold text-xs transition-all active:scale-95 border ${
                isAudioPlaying
                  ? isKindle ? 'bg-amber-200 border-amber-400 text-amber-800' : 'bg-amber-900/50 border-amber-500/50 text-amber-200 hover:bg-amber-900/70'
                  : isKindle ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'bg-emerald-900/40 border-emerald-500/40 text-emerald-200 hover:bg-emerald-900/60'
              }`}
              data-testid="toolbar-audio-btn"
            >
              {isAudioLoading ? (
                <><Headphones className="w-4 h-4 animate-pulse" /><span>Loading</span></>
              ) : isAudioPlaying ? (
                <><Pause className="w-4 h-4" /><span>Pause</span></>
              ) : canResumeAudio ? (
                <><Play className="w-4 h-4" /><span>Resume</span></>
              ) : (
                <><Headphones className="w-4 h-4" /><span>Listen</span></>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ══ ROW 2: Rew/Fwd + Focus + More ══ */}
      <div className="px-3 sm:px-4 pb-2 pt-0.5">
        <div className="flex items-center gap-2">

          {/* ── Rewind / Forward ── */}
          <div className={`flex items-center gap-0 rounded-lg border ${isKindle ? 'border-amber-300/60 bg-amber-100/30' : 'border-slate-700/40 bg-slate-800/20'}`}>
            <button
              onClick={onRewindParagraph}
              disabled={disabled || isAudioLoading}
              className={`h-8 px-2 flex items-center gap-1 rounded-l-lg text-[11px] transition-all disabled:opacity-30 ${isKindle ? 'text-amber-700 hover:bg-amber-200' : 'text-slate-500 hover:text-cyan-300 hover:bg-slate-800'}`}
              title="Rewind paragraph"
              data-testid="toolbar-rewind-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Rew</span>
            </button>
            <div className={`w-px h-5 ${isKindle ? 'bg-amber-300/40' : 'bg-slate-700/40'}`} />
            <button
              onClick={onForwardParagraph}
              disabled={disabled || isAudioLoading}
              className={`h-8 px-2 flex items-center gap-1 rounded-r-lg text-[11px] transition-all disabled:opacity-30 ${isKindle ? 'text-amber-700 hover:bg-amber-200' : 'text-slate-500 hover:text-cyan-300 hover:bg-slate-800'}`}
              title="Skip forward"
              data-testid="toolbar-forward-btn"
            >
              <span>Fwd</span>
              <RotateCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* ── FOCUS MODE toggle ── */}
          <button
            onClick={onFocusMode}
            className={`h-8 px-3 flex items-center gap-1.5 rounded-lg text-xs font-medium transition-all border ${
              focusModeActive 
                ? isKindle ? 'bg-amber-200 border-amber-400 text-amber-800' : 'bg-amber-900/40 border-amber-500/40 text-amber-300 hover:bg-amber-900/60' 
                : isKindle ? 'border-amber-300/60 text-amber-700 hover:bg-amber-200' : 'border-slate-700/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
            title={focusModeActive ? "Exit Focus Mode" : "Enter Focus Mode"}
            data-testid="toolbar-focus-mode-btn"
          >
            {focusModeActive ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            <span>{focusModeActive ? 'Exit Focus' : 'Focus'}</span>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* ── MORE TOOLS dropdown ── */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`h-8 px-3 flex items-center gap-1.5 rounded-lg text-xs font-medium transition-all border ${
                  isKindle
                    ? 'border-amber-300/60 bg-amber-100/30 text-amber-700 hover:bg-amber-200'
                    : 'border-slate-700/50 bg-slate-800/30 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }`}
                data-testid="toolbar-more-tools-btn"
              >
                <Wand2 className="w-3.5 h-3.5" />
                <span>More</span>
                <ChevronLeft className="w-3 h-3 rotate-[-90deg] opacity-50" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#0D1525] border border-slate-700/50 shadow-2xl">
              <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-widest">Story Tools</DropdownMenuLabel>
              {onStoryArchitect && (
                <DropdownMenuItem onClick={onStoryArchitect} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-story-architect-item">
                  <Map className="w-4 h-4 text-indigo-400" /><span className="text-sm">Story Architect</span>
                  <Badge className="ml-auto text-[9px] bg-indigo-900/40 text-indigo-300 border-indigo-500/30">NEW</Badge>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onStoryBible} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-story-bible-item">
                <BookMarked className="w-4 h-4 text-purple-400" /><span className="text-sm">Story Bible</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onStoryNavigator} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-navigator-item">
                <Map className="w-4 h-4 text-emerald-400" /><span className="text-sm">Where Am I? (Navigator)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onStoryDirection} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-direction-item">
                <Target className="w-4 h-4 text-indigo-400" /><span className="text-sm">Story Directions</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700/50" />
              <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-widest">Analysis</DropdownMenuLabel>
              <DropdownMenuItem onClick={onAnalyze} disabled={analyzing} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-analyze-item">
                <BookOpen className="w-4 h-4 text-blue-400" /><span className="text-sm">{analyzing ? 'Analyzing...' : 'Analyze Story'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onAnalysisDashboard} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-dashboard-item">
                <BarChart3 className="w-4 h-4 text-teal-400" /><span className="text-sm">Manuscript Dashboard</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700/50" />
              <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-widest">Enhancement</DropdownMenuLabel>
              <DropdownMenuItem onClick={onWritingTools} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-polish-item">
                <Wand2 className="w-4 h-4 text-cyan-400" /><span className="text-sm">Writing Polish Tools</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700/50" />
              <DropdownMenuLabel className="text-pink-400/80 text-[10px] uppercase tracking-widest">New Features</DropdownMenuLabel>
              <DropdownMenuItem onClick={onAIDetection} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-ai-detection-item">
                <Eye className="w-4 h-4 text-cyan-400" /><span className="text-sm">AI Detection & Humanizer</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onSynopsisLadder} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-synopsis-item">
                <FileText className="w-4 h-4 text-emerald-400" /><span className="text-sm">Synopsis Ladder</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onCanonLock} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-canon-lock-item">
                <Shield className="w-4 h-4 text-violet-400" /><span className="text-sm">Canon Lock</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onCharacterPortraits} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-portraits-item">
                <Image className="w-4 h-4 text-pink-400" /><span className="text-sm">Character Portraits</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700/50" />
              <DropdownMenuItem onClick={onContactSupport} className="cursor-pointer py-2.5 gap-2 hover:bg-slate-800/60" data-testid="toolbar-contact-support-item">
                <MessageCircle className="w-4 h-4 text-emerald-400" /><span className="text-sm">Contact Support</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default WritingToolbar;
